import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '../../app/routes.js'
import {
  LandingIcon,
  LandingLogoMark,
  type LandingIconName,
} from './LandingIcons.js'
import {
  CheckoutMockup,
  DashboardMockup,
  HeroStorefront,
} from './LandingMockups.js'
import '../../styles/landing.css'

const NAV_LINKS = [
  { href: '#features', labelKey: 'landing.nav.features' },
  { href: '#how-it-works', labelKey: 'landing.nav.how' },
  { href: '#for-guests', labelKey: 'landing.nav.guests' },
] as const

const FEATURES: {
  icon: LandingIconName
  titleKey: string
  textKey: string
}[] = [
  {
    icon: 'storefront',
    titleKey: 'landing.features.items.storefront.title',
    textKey: 'landing.features.items.storefront.text',
  },
  {
    icon: 'menu',
    titleKey: 'landing.features.items.catalog.title',
    textKey: 'landing.features.items.catalog.text',
  },
  {
    icon: 'orders',
    titleKey: 'landing.features.items.orders.title',
    textKey: 'landing.features.items.orders.text',
  },
  {
    icon: 'delivery',
    titleKey: 'landing.features.items.fulfillment.title',
    textKey: 'landing.features.items.fulfillment.text',
  },
  {
    icon: 'customers',
    titleKey: 'landing.features.items.customers.title',
    textKey: 'landing.features.items.customers.text',
  },
  {
    icon: 'dashboard',
    titleKey: 'landing.features.items.cabinet.title',
    textKey: 'landing.features.items.cabinet.text',
  },
]

const HOW_STEPS = [
  {
    titleKey: 'landing.how.steps.account.title',
    textKey: 'landing.how.steps.account.text',
  },
  {
    titleKey: 'landing.how.steps.setup.title',
    textKey: 'landing.how.steps.setup.text',
  },
  {
    titleKey: 'landing.how.steps.orders.title',
    textKey: 'landing.how.steps.orders.text',
  },
] as const

const GUEST_STEPS = [
  {
    titleKey: 'landing.guests.steps.choose.title',
    textKey: 'landing.guests.steps.choose.text',
  },
  {
    titleKey: 'landing.guests.steps.checkout.title',
    textKey: 'landing.guests.steps.checkout.text',
  },
  {
    titleKey: 'landing.guests.steps.receive.title',
    textKey: 'landing.guests.steps.receive.text',
  },
] as const

const STRIP_ITEMS = [
  'landing.strip.storefront',
  'landing.strip.menu',
  'landing.strip.modifiers',
  'landing.strip.pickup',
  'landing.strip.delivery',
  'landing.strip.orders',
  'landing.strip.customers',
  'landing.strip.schedule',
] as const

function useLandingMeta(title: string, description: string) {
  useEffect(() => {
    const previousTitle = document.title
    const existing = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    const previousDescription = existing?.content
    const meta = existing ?? document.createElement('meta')

    if (!existing) {
      meta.setAttribute('name', 'description')
      document.head.appendChild(meta)
    }

    document.title = title
    meta.setAttribute('content', description)

    return () => {
      document.title = previousTitle
      if (existing) {
        if (previousDescription !== undefined) meta.setAttribute('content', previousDescription)
      } else {
        meta.remove()
      }
    }
  }, [title, description])
}

function useDocumentLanguage(language: string) {
  useEffect(() => {
    const previous = document.documentElement.lang
    document.documentElement.lang = language
    return () => {
      document.documentElement.lang = previous
    }
  }, [language])
}

export default function LandingPage() {
  const { t, i18n } = useTranslation()
  const year = new Date().getFullYear()

  useLandingMeta(t('landing.meta.title'), t('landing.meta.description'))
  useDocumentLanguage(i18n.language)

  return (
    <div className="mf-landing">
      <a className="mf-skip-link" href="#mf-main">
        {t('landing.skipToContent')}
      </a>

      <header className="mf-header">
        <div className="mf-container mf-header-inner">
          <Link className="mf-logo" to={ROUTES.HOME} aria-label={t('landing.logoAria')}>
            <LandingLogoMark />
            <span className="mf-logo-word">
              Magic <b>Food</b>
            </span>
          </Link>

          <nav className="mf-nav" aria-label={t('landing.nav.label')}>
            {NAV_LINKS.map(link => (
              <a key={link.href} href={link.href}>
                {t(link.labelKey)}
              </a>
            ))}
          </nav>

          <div className="mf-header-actions">
            <Link className="mf-login" to={ROUTES.LOGIN}>
              {t('landing.nav.login')}
            </Link>
            <Link className="mf-btn mf-btn--primary mf-btn--sm" to={ROUTES.REGISTER}>
              <span className="mf-show-desktop">{t('landing.nav.cta')}</span>
              <span className="mf-show-mobile">{t('landing.nav.ctaShort')}</span>
            </Link>
          </div>
        </div>
      </header>

      <main id="mf-main" tabIndex={-1}>
        <section className="mf-hero" aria-labelledby="mf-hero-title">
          <div className="mf-container mf-hero-grid">
            <div className="mf-hero-copy">
              <p className="mf-eyebrow">
                <span className="mf-eyebrow-dot" />
                {t('landing.hero.eyebrow')}
              </p>

              <h1 className="mf-h1" id="mf-hero-title">
                {t('landing.hero.titleLead')}{' '}
                <span className="mf-h1-accent">{t('landing.hero.titleAccent')}</span>
              </h1>

              <p className="mf-hero-description">{t('landing.hero.description')}</p>

              <div className="mf-hero-actions">
                <Link className="mf-btn mf-btn--primary mf-btn--lg" to={ROUTES.REGISTER}>
                  {t('landing.hero.primary')}
                  <LandingIcon name="arrow-right" />
                </Link>
                <a className="mf-btn mf-btn--outline mf-btn--lg" href="#how-it-works">
                  {t('landing.hero.secondary')}
                </a>
              </div>

              <div className="mf-hero-chips">
                <span className="mf-hero-chip">
                  <LandingIcon name="check" />
                  {t('landing.hero.chips.storefront')}
                </span>
                <span className="mf-hero-chip">
                  <LandingIcon name="check" />
                  {t('landing.hero.chips.pickup')}
                </span>
                <span className="mf-hero-chip">
                  <LandingIcon name="check" />
                  {t('landing.hero.chips.delivery')}
                </span>
              </div>
            </div>

            <HeroStorefront />
          </div>

          <div className="mf-container">
            <div className="mf-strip" role="list" aria-label={t('landing.strip.label')}>
              {STRIP_ITEMS.map(key => (
                <span className="mf-strip-item" role="listitem" key={key}>
                  <i />
                  {t(key)}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="mf-section mf-features" id="features" aria-labelledby="mf-features-title">
          <div className="mf-container">
            <div className="mf-section-head">
              <p className="mf-eyebrow">{t('landing.features.eyebrow')}</p>
              <h2 className="mf-h2" id="mf-features-title">
                {t('landing.features.title')}
              </h2>
              <p className="mf-section-desc">{t('landing.features.description')}</p>
            </div>

            <div className="mf-features-grid">
              {FEATURES.map((feature, index) => (
                <article
                  className={index === 0 ? 'mf-feature mf-feature--lead' : 'mf-feature'}
                  key={feature.titleKey}
                >
                  <span className="mf-feature-icon">
                    <LandingIcon name={feature.icon} />
                  </span>
                  <h3>{t(feature.titleKey)}</h3>
                  <p>{t(feature.textKey)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          className="mf-section mf-how"
          id="how-it-works"
          aria-labelledby="mf-how-title"
        >
          <div className="mf-container mf-how-grid">
            <div className="mf-how-copy">
              <p className="mf-eyebrow mf-eyebrow--light">{t('landing.how.eyebrow')}</p>
              <h2 className="mf-h2" id="mf-how-title">
                {t('landing.how.title')}
              </h2>
              <p className="mf-section-desc">{t('landing.how.description')}</p>

              <ol className="mf-steps">
                {HOW_STEPS.map((step, index) => (
                  <li className="mf-step" key={step.titleKey}>
                    <span className="mf-step-num">{String(index + 1).padStart(2, '0')}</span>
                    <span className="mf-step-copy">
                      <h3>{t(step.titleKey)}</h3>
                      <p>{t(step.textKey)}</p>
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mf-how-mock">
              <DashboardMockup />
            </div>
          </div>
        </section>

        <section
          className="mf-section mf-guests"
          id="for-guests"
          aria-labelledby="mf-guests-title"
        >
          <div className="mf-container mf-guests-grid">
            <div className="mf-guests-copy">
              <p className="mf-eyebrow">{t('landing.guests.eyebrow')}</p>
              <h2 className="mf-h2" id="mf-guests-title">
                {t('landing.guests.title')}
              </h2>
              <p className="mf-section-desc">{t('landing.guests.description')}</p>

              <ol className="mf-guest-steps">
                {GUEST_STEPS.map(step => (
                  <li className="mf-guest-step" key={step.titleKey}>
                    <span className="mf-guest-step-icon">
                      <LandingIcon name="check" />
                    </span>
                    <span className="mf-guest-step-copy">
                      <h3>{t(step.titleKey)}</h3>
                      <p>{t(step.textKey)}</p>
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mf-guests-mock">
              <CheckoutMockup />
            </div>
          </div>
        </section>

        <section className="mf-section mf-cta-section" aria-labelledby="mf-cta-title">
          <div className="mf-container">
            <div className="mf-cta-panel">
              <span className="mf-cta-spark" aria-hidden="true">
                <LandingIcon name="spark" />
              </span>
              <div className="mf-cta-grid">
                <div className="mf-cta-copy">
                  <p className="mf-eyebrow mf-eyebrow--ink">{t('landing.cta.eyebrow')}</p>
                  <h2 className="mf-h2" id="mf-cta-title">
                    {t('landing.cta.title')}
                  </h2>
                  <p>{t('landing.cta.description')}</p>
                </div>
                <div className="mf-cta-actions">
                  <Link className="mf-btn mf-btn--primary mf-btn--lg" to={ROUTES.REGISTER}>
                    {t('landing.cta.primary')}
                    <LandingIcon name="arrow-right" />
                  </Link>
                  <Link className="mf-btn mf-btn--outline mf-btn--lg" to={ROUTES.LOGIN}>
                    {t('landing.cta.secondary')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="mf-footer">
        <div className="mf-container">
          <div className="mf-footer-grid">
            <div className="mf-footer-brand">
              <Link className="mf-logo mf-logo--light" to={ROUTES.HOME} aria-label={t('landing.logoAria')}>
                <LandingLogoMark />
                <span className="mf-logo-word">
                  Magic <b>Food</b>
                </span>
              </Link>
              <p>{t('landing.footer.tagline')}</p>
            </div>

            <nav className="mf-footer-nav" aria-labelledby="mf-footer-nav-title">
              <h3 className="mf-footer-title" id="mf-footer-nav-title">
                {t('landing.footer.navTitle')}
              </h3>
              {NAV_LINKS.map(link => (
                <a key={link.href} href={link.href}>
                  {t(link.labelKey)}
                </a>
              ))}
            </nav>

            <nav className="mf-footer-nav" aria-labelledby="mf-footer-account-title">
              <h3 className="mf-footer-title" id="mf-footer-account-title">
                {t('landing.footer.accountTitle')}
              </h3>
              <Link to={ROUTES.LOGIN}>{t('landing.footer.login')}</Link>
              <Link to={ROUTES.REGISTER}>{t('landing.footer.register')}</Link>
            </nav>
          </div>

          <div className="mf-footer-bottom">
            <span>
              © {year} Magic Food. {t('landing.footer.rights')}
            </span>
            <span className="mf-footer-domain">magic-food.online</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
